/**
 * One compact overflow for an item being customised (#1860).
 *
 * The grip stays outside this menu because dragging and opening a menu are two
 * different gestures. Everything else belongs behind one predictable button:
 * moving without drag, changing section, pinning, hiding, and authoring a
 * section. Hosts supply callbacks; this component fetches and stores nothing.
 *
 * Mutation proof, executed against the conformance fixture and then restored:
 * forcing a hidden row to say Hide red 1; offering the current section as a
 * move destination red 1; making every trigger merely "More options" red 2;
 * dropping Radix's disabled state from an unavailable move red 1.
 */
import {
  ArrowDown, ArrowUp, Eye, EyeOff, FolderInput, FolderPlus,
  MoreVertical, Pencil, Pin, PinOff, SlidersHorizontal, Trash2,
} from 'lucide-react'
import { useRef, type RefObject } from 'react'
import { Button } from '../controls/Button'
import { Menu } from '../controls/Menu'
import s from './CustomizeRowMenu.module.css'

export interface CustomizeSectionTarget {
  key: string
  label: string
  onSelect: () => void
  current?: boolean
}

export interface CustomizeRowMenuProps {
  label: string
  triggerRef?: RefObject<HTMLButtonElement | null>
  hidden?: boolean
  pinned?: boolean
  canMoveUp?: boolean
  canMoveDown?: boolean
  sections?: readonly CustomizeSectionTarget[]
  onMoveUp?: () => void
  onMoveDown?: () => void
  onToggleHidden?: () => void
  onTogglePinned?: () => void
  onAddSection?: () => void
  onRename?: () => void
  onDelete?: () => void
  actions?: readonly { key: string; label: string; onSelect: () => void; danger?: boolean }[]
}

export function CustomizeRowMenu({
  label, triggerRef, hidden, pinned, canMoveUp = true, canMoveDown = true, sections,
  onMoveUp, onMoveDown, onToggleHidden, onTogglePinned, onAddSection,
  onRename, onDelete, actions,
}: CustomizeRowMenuProps) {
  const hasEdit = onRename || onDelete
  const hasPosition = onMoveUp || onMoveDown || sections?.some((section) => !section.current)
  /* A rename replaces this row's label with an autofocus input. Radix normally
     restores focus to the three-dot trigger after a menu action; that focus
     immediately blurred the new input and committed it before anybody could
     type. Suppress restoration for this one hand-off so the editor owns focus. */
  const focusMovesIntoRow = useRef(false)

  return (
    <Menu
      align="end"
      testId="customize-row-menu"
      onCloseAutoFocus={(event) => {
        if (!focusMovesIntoRow.current) return
        event.preventDefault()
        focusMovesIntoRow.current = false
      }}
      trigger={(
        <Button ref={triggerRef} type="button" variant="ghost" className={s.trigger}
          icon={<MoreVertical />} aria-label={`More options for ${label}`} />
      )}
    >
      <Menu.Label>{label}</Menu.Label>
      {actions?.map((action) => (
        <Menu.Item key={action.key} onSelect={action.onSelect} danger={action.danger}>
          <span className={s.item}><SlidersHorizontal /><span>{action.label}</span></span>
        </Menu.Item>
      ))}
      {actions?.length && (onTogglePinned || onToggleHidden || hasPosition || hasEdit)
        ? <Menu.Separator /> : null}
      {onTogglePinned && (
        <Menu.Item onSelect={onTogglePinned} testId="customize-row-pin">
          <span className={s.item}>{pinned ? <PinOff /> : <Pin />}<span>{pinned ? 'Unpin' : 'Pin'}</span></span>
        </Menu.Item>
      )}
      {onToggleHidden && (
        <Menu.Item onSelect={onToggleHidden} testId="customize-row-visibility">
          <span className={s.item}>{hidden ? <Eye /> : <EyeOff />}<span>{hidden ? 'Show' : 'Hide'}</span></span>
        </Menu.Item>
      )}
      {hasPosition && <Menu.Separator />}
      {onMoveUp && (
        <Menu.Item onSelect={onMoveUp} disabled={!canMoveUp}>
          <span className={s.item}><ArrowUp /><span>Move up</span></span>
        </Menu.Item>
      )}
      {onMoveDown && (
        <Menu.Item onSelect={onMoveDown} disabled={!canMoveDown}>
          <span className={s.item}><ArrowDown /><span>Move down</span></span>
        </Menu.Item>
      )}
      {sections?.filter((section) => !section.current).map((section) => (
        <Menu.Item key={section.key} onSelect={section.onSelect}>
          <span className={s.item}><FolderInput /><span>Move to {section.label}</span></span>
        </Menu.Item>
      ))}
      {onAddSection && (
        <Menu.Item onSelect={onAddSection}>
          <span className={s.item}><FolderPlus /><span>Add to new section</span></span>
        </Menu.Item>
      )}
      {hasEdit && <Menu.Separator />}
      {onRename && (
        <Menu.Item onSelect={() => { focusMovesIntoRow.current = true; onRename() }}>
          <span className={s.item}><Pencil /><span>Rename section</span></span>
        </Menu.Item>
      )}
      {onDelete && (
        <Menu.Item onSelect={onDelete} danger>
          <span className={s.item}><Trash2 /><span>Delete section</span></span>
        </Menu.Item>
      )}
    </Menu>
  )
}
